import React, { useCallback, useContext, useMemo, useState } from 'react';
import { updateFulfillmentPlanRecipientTaxIdentifiers } from '../../../../../../../fulfillment-plans/fulfillment-plans.api';
import { useUpdateEntitiesFulfillmentPlans } from '../../../../../hooks/use-update-entities-fulfillment-plans';
import { useGetBulkUpdatableFulfillmentPlanIds } from './hooks';
import { TaxIdentifierSchema } from './schema';
import { useYupFormValidation } from '../../../../../../../validation/use-yup-form-validation';

const EditRecipientTaxIdentifiersFormContext = React.createContext({});
export const useEditRecipientTaxIdentifiersFormContext = () => useContext(EditRecipientTaxIdentifiersFormContext);

/** @typedef { import('./types').TaxIdentifierType}  TaxIdentifierType */
/**
 * @param {object} props
 * @param {JSX.Element} props.children
 * @param {string} props.fulfillmentPlanId - selected fulfillmentPlanId to configure tax identifiers
 * @param {TaxIdentifierType[]} props.initialTaxIdentifiers - intial tax identifiers to be edited
 * @param {string} props.salesOrderId - selected salesOrderId to configure tax identifiers
 * @returns {JSX.Element} The rendered EditRecipientTaxIdentifiersFormProvider
 */
export const EditRecipientTaxIdentifiersFormProvider = ({ children, fulfillmentPlanId, initialTaxIdentifiers = [], salesOrderId }) => {
  const [taxIdentifiers, setTaxIdentifiers] = useState(initialTaxIdentifiers?.length ? initialTaxIdentifiers : [{}]);

  const { errors: validationErrors, isValid } = useYupFormValidation({
    form: taxIdentifiers,
    schema: TaxIdentifierSchema,
  });

  const [isBulkUpdate, setIsBulkUpdate] = useState(false);
  const [isApiError, setIsApiError] = useState(false);
  const [touched, setTouched] = useState(false);

  const bulkFulfillmentPlanIds = useGetBulkUpdatableFulfillmentPlanIds(salesOrderId);
  const updateEntities = useUpdateEntitiesFulfillmentPlans();

  const addNewTaxId = useCallback(() => {
    if (!taxIdentifiers.length) {
      setTaxIdentifiers([{}, {}]);
    } else {
      setTaxIdentifiers((prev) => [...prev, {}]);
    }
  }, [taxIdentifiers]);

  const removeTaxId = useCallback(
    (id) => {
      setTaxIdentifiers(taxIdentifiers.filter((_, i) => i !== id));
    },
    [taxIdentifiers],
  );

  const taxIdentifierApiAction = useCallback(
    async (payload) => {
      try {
        const { data } = await updateFulfillmentPlanRecipientTaxIdentifiers(payload);
        updateEntities(data);
        setIsApiError(false);
        return true;
      } catch {
        setIsApiError(true);
        return false;
      }
    },
    [updateEntities],
  );

  const applyToAllFulfillmentPlans = useCallback(() => {
    setIsBulkUpdate((prevState) => !prevState);
  }, []);

  const updateTaxIdentifiers = useCallback(
    (newValues, id) => {
      const newTaxIds = !taxIdentifiers.length
        ? [newValues]
        : taxIdentifiers.map((taxId, i) => {
            if (id === i) {
              const newTaxId = {
                ...taxId,
                redactedTaxNumber: undefined,
                ...newValues,
              };
              return newTaxId;
            }
            return taxId;
          });
      setTaxIdentifiers(newTaxIds);
      setTouched(true);
    },
    [taxIdentifiers],
  );

  const clearTaxIdentifier = useCallback(() => {
    setTaxIdentifiers([]);
    setTouched(true);
  }, []);

  const buildPayload = useCallback(
    () =>
      taxIdentifiers.map((taxId) => {
        if (taxId.redactedTaxNumber && !taxId.taxNumber) {
          return {
            type: taxId.type,
            taxNumber: taxId.redactedTaxNumber,
            isModified: false,
          };
        }
        return taxId;
      }),
    [taxIdentifiers],
  );

  const saveTaxIdentifier = useCallback(async () => {
    setTouched(false);
    const isEmptyPayload = taxIdentifiers.length === 0 || !isValid;
    const fulfillmentPlanIds = isBulkUpdate ? bulkFulfillmentPlanIds : [fulfillmentPlanId];
    const recipientTaxIdentifiers = isEmptyPayload ? null : buildPayload();
    const payload = {
      fulfillmentPlanIds,
      recipientTaxIdentifiers: recipientTaxIdentifiers || [],
    };

    if (fulfillmentPlanIds.length === 0) {
      setIsApiError(true);
      return false;
    }

    return taxIdentifierApiAction(payload);
  }, [taxIdentifiers.length, isValid, isBulkUpdate, bulkFulfillmentPlanIds, fulfillmentPlanId, buildPayload, taxIdentifierApiAction]);

  const value = useMemo(
    () => ({
      taxIdentifiers,
      addNewTaxId,
      isApiError,
      saveTaxIdentifier,
      isBulkUpdate,
      applyToAllFulfillmentPlans,
      saveDisabled: touched && !isValid,
      clearTaxIdentifier,
      updateTaxIdentifiers,
      validationErrors,
      removeTaxId,
    }),
    [
      taxIdentifiers,
      addNewTaxId,
      isApiError,
      saveTaxIdentifier,
      isBulkUpdate,
      applyToAllFulfillmentPlans,
      updateTaxIdentifiers,
      validationErrors,
      clearTaxIdentifier,
      removeTaxId,
      isValid,
      touched,
    ],
  );

  return <EditRecipientTaxIdentifiersFormContext.Provider value={value}>{children}</EditRecipientTaxIdentifiersFormContext.Provider>;
};
